import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useEvents } from '../../contexts/EventsContext';
import { useMaps } from '../../contexts/MapsContext';
import { mapsAPI, qrAPI, eventAPI } from '../../services/api';
import EditMapModal from './EditMapModal';
import MapDetailModal from './MapDetailModal';
import MapCreationModal from '../maps/MapCreationModal';
import NotificationsModal from './NotificationsModal';
import CollaboratorsManager from '../maps/CollaboratorsManager';
import {
  ShoppingBag, PartyPopper, Building2, Briefcase, MapPin,
  RefreshCw, Map, Link2, QrCode, Pencil, BarChart2, Users,
  Trash2, Plus, ArrowLeft, Eye, MapPinned, Calendar,
  FolderOpen, Package, ChevronRight, Zap,
} from 'lucide-react';
import './MyMaps.css';

const CATEGORY_META = {
  market: { label: 'Market', Icon: ShoppingBag },
  festival: { label: 'Festival', Icon: PartyPopper },
  municipal: { label: 'Municipal', Icon: Building2 },
  corporate: { label: 'Corporate', Icon: Briefcase },
  other: { label: 'Other', Icon: MapPin },
};

const RECURRENCE_LABELS = {
  'one-time': 'One-time',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

const COLOR_OPTIONS = [
  '#2B8EE8', '#0EA5E9', '#10B981', '#F59E0B',
  '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6',
];

// ── Create Event Modal ───────────────────────────────────────
const CreateEventModal = ({ isOpen, onClose, onCreate }) => {
  const [form, setForm] = useState({
    title: '', description: '', category: 'other',
    recurrence: 'one-time', coverColor: '#2B8EE8',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    try {
      setLoading(true); setError(null);
      await onCreate(form);
      setForm({ title: '', description: '', category: 'other', recurrence: 'one-time', coverColor: '#2B8EE8' });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create event');
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="mm-overlay" onClick={onClose}>
      <div className="mm-modal mm-modal--sm" onClick={e => e.stopPropagation()}>
        <div className="mm-modal-header">
          <h2 className="mm-modal-title">Create Event</h2>
          <button className="mm-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="mm-modal-body">
          <div className="mm-form-group">
            <label>Event Name *</label>
            <input className="mm-input" placeholder="e.g. Tel Aviv Night Market"
              value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="mm-form-group">
            <label>Description</label>
            <textarea className="mm-input mm-textarea" placeholder="What is this event about?" rows={3}
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="mm-form-row">
            <div className="mm-form-group">
              <label>Category</label>
              <select className="mm-input" value={form.category}
                onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {Object.entries(CATEGORY_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="mm-form-group">
              <label>Recurrence</label>
              <select className="mm-input" value={form.recurrence}
                onChange={e => setForm(p => ({ ...p, recurrence: e.target.value }))}>
                {Object.entries(RECURRENCE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mm-form-group">
            <label>Color</label>
            <div className="mm-color-row">
              {COLOR_OPTIONS.map(c => (
                <button key={c} className={`mm-color-swatch ${form.coverColor === c ? 'mm-color-swatch--active' : ''}`}
                  style={{ background: c }} onClick={() => setForm(p => ({ ...p, coverColor: c }))} />
              ))}
            </div>
          </div>
          {error && <p className="mm-error">{error}</p>}
        </div>
        <div className="mm-modal-footer">
          <button className="mm-btn mm-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="mm-btn mm-btn--primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating…' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Assign Event Modal ───────────────────────────────────────
const AssignEventModal = ({ map, events, isOpen, onClose, onAssign }) => {
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAssign = async () => {
    if (!selectedEventId) { setError('Please select an event'); return; }
    try {
      setLoading(true); setError(null);
      await onAssign(map.id, selectedEventId);
      setSelectedEventId(''); onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign map');
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="mm-overlay" onClick={onClose}>
      <div className="mm-modal mm-modal--sm" onClick={e => e.stopPropagation()}>
        <div className="mm-modal-header">
          <h2 className="mm-modal-title">Assign to Event</h2>
          <button className="mm-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="mm-modal-body">
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#5A6A82' }}>
            Assign <strong>{map?.title}</strong> to an event folder.
          </p>
          <div className="mm-form-group">
            <label>Select Event</label>
            <select className="mm-input" value={selectedEventId}
              onChange={e => setSelectedEventId(e.target.value)}>
              <option value="">Choose an event…</option>
              {events.map(event => (
                <option key={event._id} value={event._id}>{event.title}</option>
              ))}
            </select>
          </div>
          {error && <p className="mm-error">{error}</p>}
        </div>
        <div className="mm-modal-footer">
          <button className="mm-btn mm-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="mm-btn mm-btn--primary" onClick={handleAssign} disabled={loading}>
            {loading ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Event Collaborators Modal ────────────────────────────────
const EventCollaboratorsModal = ({ event, isOpen, onClose }) => {
  const [collaborators, setCollaborators] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (!isOpen || !event) return;
    const fetchCollabs = async () => {
      try {
        setFetchLoading(true); setError(null);
        const res = await eventAPI.getCollaborators(event._id);
        setCollaborators(res.data.data.collaborators);
        setIsOwner(res.data.data.isOwner);
      } catch { setError('Failed to load collaborators'); }
      finally { setFetchLoading(false); }
    };
    fetchCollabs();
  }, [isOpen, event]);

  const handleAdd = async () => {
    if (!email.trim()) { setError('Email is required'); return; }
    try {
      setLoading(true); setError(null); setSuccess(null);
      const res = await eventAPI.addCollaborator(event._id, email.trim());
      setCollaborators(prev => [...prev, { _id: res.data.data.collaboratorId, email: res.data.data.email }]);
      setEmail(''); setSuccess('Collaborator added');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add collaborator');
    } finally { setLoading(false); }
  };

  const handleRemove = async (userId) => {
    try {
      setError(null);
      await eventAPI.removeCollaborator(event._id, userId);
      setCollaborators(prev => prev.filter(c => c._id !== userId));
    } catch { setError('Failed to remove collaborator'); }
  };

  if (!isOpen) return null;

  return (
    <div className="mm-overlay" onClick={onClose}>
      <div className="mm-modal mm-modal--sm" onClick={e => e.stopPropagation()}>
        <div className="mm-modal-header">
          <h2 className="mm-modal-title">Event Access</h2>
          <button className="mm-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="mm-modal-body">
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#5A6A82' }}>
            Collaborators can view and edit all maps inside <strong>{event?.title}</strong>.
          </p>
          {isOwner && (
            <div className="mm-collab-add">
              <div className="mm-collab-input-row">
                <input className="mm-input" placeholder="Add by email address"
                  value={email} onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAdd()} />
                <button className="mm-btn mm-btn--primary" onClick={handleAdd} disabled={loading}>
                  {loading ? '…' : 'Add'}
                </button>
              </div>
              {error && <p className="mm-error">{error}</p>}
              {success && <p className="mm-success">{success}</p>}
            </div>
          )}
          {fetchLoading ? (
            <div className="mm-loading"><div className="mm-spinner" /></div>
          ) : collaborators.length === 0 ? (
            <p style={{ fontSize: '0.875rem', color: '#94A3B8', textAlign: 'center', padding: '1rem 0' }}>
              No collaborators yet.
            </p>
          ) : (
            <div className="mm-collab-list">
              {collaborators.map(c => (
                <div key={c._id} className="mm-collab-item">
                  <div className="mm-collab-avatar">{(c.name || c.email)[0].toUpperCase()}</div>
                  <div className="mm-collab-info">
                    {c.name && <span className="mm-collab-name">{c.name}</span>}
                    <span className="mm-collab-email">{c.email}</span>
                  </div>
                  {isOwner && (
                    <button className="mm-icon-btn mm-icon-btn--danger"
                      onClick={() => handleRemove(c._id)} title="Remove">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mm-modal-footer">
          <button className="mm-btn mm-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Event Folder Card ────────────────────────────────────────
const EventFolder = ({ event, onOpen, onArchive, onDelete, onManageAccess }) => {
  const { Icon } = CATEGORY_META[event.category] || CATEGORY_META.other;
  const cat = CATEGORY_META[event.category] || CATEGORY_META.other;
  const rec = RECURRENCE_LABELS[event.recurrence] || '';

  return (
    <div className="mm-folder" onClick={() => onOpen(event)}>
      <div className="mm-folder-accent" style={{ background: event.coverColor || '#2B8EE8' }} />
      <div className="mm-folder-body">
        <div className="mm-folder-top">
          <div className="mm-folder-icon">
            <Icon size={18} />
          </div>
          <div className="mm-folder-badges">
            <span className="mm-badge mm-badge--cat">{cat.label}</span>
            {event.recurrence !== 'one-time' && (
              <span className="mm-badge mm-badge--rec">
                <RefreshCw size={9} style={{ display: 'inline', marginRight: 3 }} />{rec}
              </span>
            )}
          </div>
        </div>
        <h3 className="mm-folder-title">{event.title}</h3>
        {event.description && <p className="mm-folder-desc">{event.description}</p>}
        <div className="mm-folder-stats">
          <span className="mm-folder-stat">
            <Map size={12} />{event.mapCount || 0} map{event.mapCount !== 1 ? 's' : ''}
          </span>
          {event.activeMaps > 0 && (
            <span className="mm-folder-active">
              <Zap size={11} />{event.activeMaps} active
            </span>
          )}
        </div>
      </div>
      <div className="mm-folder-actions" onClick={e => e.stopPropagation()}>
        <button className="mm-icon-btn" title="Manage access" onClick={() => onManageAccess(event)}>
          <Users size={15} />
        </button>
        <button className="mm-icon-btn" title="Archive" onClick={() => onArchive(event)}>
          <Package size={15} />
        </button>
        <button className="mm-icon-btn mm-icon-btn--danger" title="Delete" onClick={() => onDelete(event)}>
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
};

// ── Map Card ─────────────────────────────────────────────────
const MapCard = ({ map, onView, onEdit, onDelete, onManageCollaborators, onNotify }) => {
  const navigate = useNavigate();

  const copyShareUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(map.shareUrl);
    alert('Share URL copied!');
  };

  const handleViewQR = (e) => {
    e.stopPropagation();
    const url = qrAPI.getQRCodeUrl(map.base44Code, { size: 400, download: true });
    window.open(url, '_blank');
  };

  const canManage = map.isOwner !== false;

  return (
    <div className="mm-map-card">
      <div className="mm-map-card-header">
        <div>
          <h4 className="mm-map-title" onClick={() => onView(map)}>{map.title}</h4>
          {map.occurrence && <p className="mm-map-occurrence">{map.occurrence}</p>}
        </div>
        <span className={`mm-status ${map.isCurrentlyActive ? 'mm-status--active' : 'mm-status--inactive'}`}>
          <span className="mm-status-dot" />
          {map.isCurrentlyActive ? 'Active' : 'Inactive'}
        </span>
      </div>
      {map.description && <p className="mm-map-desc">{map.description}</p>}
      <div className="mm-map-stats">
        <span className="mm-map-stat"><Eye size={12} />{map.visitCount || 0} visits</span>
        <span className="mm-map-stat"><MapPinned size={12} />{map.markersCount || 0} markers</span>
        <span className="mm-map-stat"><Calendar size={12} />{new Date(map.startTime).toLocaleDateString()}</span>
      </div>
      <div className="mm-map-actions">
        <button className="mm-btn mm-btn--primary mm-btn--sm" onClick={() => onView(map)}>
          <Map size={13} />View
        </button>
        <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={copyShareUrl}>
          <Link2 size={13} />Link
        </button>
        <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={handleViewQR}>
          <QrCode size={13} />QR
        </button>
        <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => onEdit(map)}>
          <Pencil size={13} />Edit
        </button>
        <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => navigate(`/analytics/${map.id}`)}>
          <BarChart2 size={13} />Analytics
        </button>
        {canManage && (
          <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => onNotify(map)}>
            <Bell size={13} />Notify
          </button>
        )}
        {canManage && (
          <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => onManageCollaborators(map)}>
            <Users size={13} />Access
          </button>
        )}
        {canManage && (
          <button className="mm-btn mm-btn--danger mm-btn--sm" onClick={() => onDelete(map)}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

// ── Event Detail Panel ───────────────────────────────────────
const EventPanel = ({ event, onClose }) => {
  const { refreshMaps, deleteMap } = useMaps();
  const navigate = useNavigate();
  const [maps, setMaps] = useState(event.maps || []);
  const [editingMap, setEditingMap] = useState(null);
  const [viewingMap, setViewingMap] = useState(null);
  const [managingCollaborators, setManagingCollaborators] = useState(null);
  const [notificationsMap, setNotificationsMap] = useState(null);
  const [showCreateMap, setShowCreateMap] = useState(false);
  const { Icon } = CATEGORY_META[event.category] || CATEGORY_META.other;

  const handleMapCreated = (newMap) => {
    setMaps(prev => [newMap, ...prev]);
    setShowCreateMap(false);
    refreshMaps();
  };

  const handleDeleteMap = async (map) => {
    if (!window.confirm(`Delete "${map.title}"? This cannot be undone.`)) return;
    await deleteMap(map.id);
    setMaps(prev => prev.filter(m => m.id !== map.id));
    refreshMaps();
  };

  return (
    <div className="mm-panel">
      <div className="mm-panel-header">
        <div className="mm-panel-header-top">
          <button className="mm-back-btn" onClick={onClose}>
            <ArrowLeft size={15} /> Back to events
          </button>
          <button className="mm-btn mm-btn--primary mm-btn--sm" onClick={() => setShowCreateMap(true)}>
            <Plus size={14} />Add Map
          </button>
        </div>
        <div className="mm-panel-title-row">
          <div className="mm-panel-icon"><Icon size={20} /></div>
          <div>
            <h2 className="mm-panel-title">{event.title}</h2>
            <p className="mm-panel-meta">
              {CATEGORY_META[event.category]?.label} · {RECURRENCE_LABELS[event.recurrence]}
              {event.description ? ` · ${event.description}` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="mm-panel-body">
        {maps.length === 0 ? (
          <div className="mm-empty">
            <div className="mm-empty-icon"><Map size={24} /></div>
            <h4>No maps yet</h4>
            <p>Add your first map to this event.</p>
            <button className="mm-btn mm-btn--primary" onClick={() => setShowCreateMap(true)}>
              <Plus size={15} />Add Map
            </button>
          </div>
        ) : (
          <div className="mm-maps-list">
            {maps.map(map => (
              <MapCard key={map.id} map={map}
                onView={setViewingMap} onEdit={setEditingMap}
                onDelete={handleDeleteMap} onManageCollaborators={setManagingCollaborators}
                onNotify={setNotificationsMap} />
            ))}
          </div>
        )}
      </div>

      {editingMap && (
        <EditMapModal map={editingMap} isOpen={!!editingMap}
          onClose={() => setEditingMap(null)}
          onSave={() => { setEditingMap(null); refreshMaps(); }} />
      )}
      {viewingMap && (
        <MapDetailModal map={viewingMap} isOpen={!!viewingMap}
          onClose={() => setViewingMap(null)} />
      )}
      {managingCollaborators && (
        <CollaboratorsManager mapId={managingCollaborators.id}
          isOwner={managingCollaborators.isOwner !== false}
          onClose={() => setManagingCollaborators(null)} />
      )}
      {notificationsMap && (
        <NotificationsModal
          map={notificationsMap}
          onClose={() => setNotificationsMap(null)} />
      )}
      {showCreateMap && (
        <MapCreationModal isOpen={showCreateMap}
          onClose={() => setShowCreateMap(false)}
          onMapCreated={handleMapCreated} eventId={event._id} />
      )}
    </div>
  );
};

// ── Main MyMaps Component ────────────────────────────────────
const MyMaps = () => {
  const {
    events, archivedEvents, loading, error,
    createEvent, toggleArchive, unarchiveEvent, deleteEvent,
    fetchArchived, getEventWithMaps
  } = useEvents();
  const { deleteMap, refreshMaps } = useMaps();
  const navigate = useNavigate();

  const [tab, setTab] = useState('active');
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [openEvent, setOpenEvent] = useState(null);
  const [unassignedMaps, setUnassignedMaps] = useState([]);
  const [archivedUnassignedMaps, setArchivedUnassignedMaps] = useState([]);
  const [unassignedTab, setUnassignedTab] = useState('active');
  const [editingMap, setEditingMap] = useState(null);
  const [viewingMap, setViewingMap] = useState(null);
  const [managingCollaborators, setManagingCollaborators] = useState(null);
  const [assigningMap, setAssigningMap] = useState(null);
  const [managingEventAccess, setManagingEventAccess] = useState(null);
  // ── NEW: notifications state for unassigned maps ──
  const [notificationsMap, setNotificationsMap] = useState(null);

  const fetchUnassigned = useCallback(async () => {
    try {
      const response = await mapsAPI.getUnassigned();
      setUnassignedMaps(response.data.data.maps);
    } catch (err) { console.error('Failed to fetch unassigned maps:', err); }
  }, []);

  const fetchArchivedUnassigned = useCallback(async () => {
    try {
      const response = await mapsAPI.getArchivedUnassigned();
      setArchivedUnassignedMaps(response.data.data.maps);
    } catch (err) { console.error('Failed to fetch archived unassigned maps:', err); }
  }, []);

  useEffect(() => { fetchUnassigned(); }, [fetchUnassigned]);

  const handleUnassignedTabChange = (t) => {
    setUnassignedTab(t);
    if (t === 'archived') fetchArchivedUnassigned();
  };

  const handleArchiveUnassignedMap = useCallback(async (map) => {
    try {
      await mapsAPI.toggleArchive(map.id);
      setUnassignedMaps(prev => prev.filter(m => m.id !== map.id));
      if (unassignedTab === 'archived') fetchArchivedUnassigned();
    } catch (err) {
      console.error('Failed to archive map:', err);
    }
  }, [unassignedTab, fetchArchivedUnassigned]);

  const handleRestoreUnassignedMap = useCallback(async (map) => {
    try {
      await mapsAPI.toggleArchive(map.id);
      setArchivedUnassignedMaps(prev => prev.filter(m => m.id !== map.id));
      fetchUnassigned();
    } catch (err) {
      console.error('Failed to restore map:', err);
    }
  }, [fetchUnassigned]);

  const handleAssignMap = useCallback(async (mapId, eventId) => {
    await mapsAPI.assignToEvent(mapId, eventId);
    setUnassignedMaps(prev => prev.filter(m => m.id !== mapId));
    refreshMaps();
  }, [refreshMaps]);

  const handleOpenEvent = useCallback(async (event) => {
    const full = await getEventWithMaps(event._id);
    setOpenEvent(full);
  }, [getEventWithMaps]);

  const handleArchive = useCallback(async (event) => {
    if (!window.confirm(`Archive "${event.title}"?`)) return;
    await toggleArchive(event._id);
  }, [toggleArchive]);

  const handleDelete = useCallback(async (event) => {
    if (!window.confirm(`Delete "${event.title}"? Maps inside will be kept but unlinked.`)) return;
    await deleteEvent(event._id);
  }, [deleteEvent]);

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'archived') fetchArchived();
  };

  if (openEvent) return <EventPanel event={openEvent} onClose={() => setOpenEvent(null)} />;

  return (
    <div className="mm-root">
      <div className="mm-header">
        <div>
          <h3 className="mm-title">My Events</h3>
          <p className="mm-subtitle">Organize your maps by event</p>
        </div>
        <button className="mm-btn mm-btn--primary" onClick={() => setShowCreateEvent(true)}>
          <Plus size={16} />New Event
        </button>
      </div>

      <div className="mm-tabs">
        <button className={`mm-tab ${tab === 'active' ? 'mm-tab--active' : ''}`}
          onClick={() => handleTabChange('active')}>
          Active {events.length > 0 && <span className="mm-tab-count">{events.length}</span>}
        </button>
        <button className={`mm-tab ${tab === 'archived' ? 'mm-tab--active' : ''}`}
          onClick={() => handleTabChange('archived')}>
          Archived {archivedEvents.length > 0 && <span className="mm-tab-count">{archivedEvents.length}</span>}
        </button>
      </div>

      {loading ? (
        <div className="mm-loading"><div className="mm-spinner" /></div>
      ) : error ? (
        <div className="mm-error-box">{error}</div>
      ) : tab === 'active' ? (
        <>
          {events.length === 0 ? (
            <div className="mm-empty">
              <div className="mm-empty-icon"><FolderOpen size={26} /></div>
              <h4>No events yet</h4>
              <p>Create your first event folder to start organizing your maps.</p>
              <button className="mm-btn mm-btn--primary" onClick={() => setShowCreateEvent(true)}>
                <Plus size={15} />Create Event
              </button>
            </div>
          ) : (
            <div className="mm-folders-grid">
              {events.map(event => (
                <EventFolder key={event._id} event={event}
                  onOpen={handleOpenEvent} onArchive={handleArchive}
                  onDelete={handleDelete} onManageAccess={setManagingEventAccess} />
              ))}
            </div>
          )}

          {/* ── Unassigned Maps Section ── */}
          {(unassignedMaps.length > 0 || unassignedTab === 'archived') && (
            <div className="mm-unassigned">
              <div className="mm-unassigned-header">
                <div>
                  <h4 className="mm-unassigned-title">Unassigned Maps</h4>
                  <p className="mm-unassigned-subtitle">These maps are not linked to any event yet.</p>
                </div>
              </div>

              <div className="mm-tabs mm-tabs--sub">
                <button
                  className={`mm-tab mm-tab--sm ${unassignedTab === 'active' ? 'mm-tab--active' : ''}`}
                  onClick={() => handleUnassignedTabChange('active')}>
                  Active {unassignedMaps.length > 0 && <span className="mm-tab-count">{unassignedMaps.length}</span>}
                </button>
                <button
                  className={`mm-tab mm-tab--sm ${unassignedTab === 'archived' ? 'mm-tab--active' : ''}`}
                  onClick={() => handleUnassignedTabChange('archived')}>
                  Archived {archivedUnassignedMaps.length > 0 && <span className="mm-tab-count">{archivedUnassignedMaps.length}</span>}
                </button>
              </div>

              {unassignedTab === 'active' ? (
                unassignedMaps.length === 0 ? (
                  <div className="mm-empty" style={{ padding: '1.5rem 0' }}>
                    <p style={{ color: '#94A3B8', fontSize: '0.875rem' }}>No active unassigned maps.</p>
                  </div>
                ) : (
                  <div className="mm-maps-list">
                    {unassignedMaps.map(map => (
                      <div key={map.id} className="mm-map-card">
                        <div className="mm-map-card-header">
                          <h4 className="mm-map-title" onClick={() => setViewingMap(map)}>{map.title}</h4>
                          <span className={`mm-status ${map.isCurrentlyActive ? 'mm-status--active' : 'mm-status--inactive'}`}>
                            <span className="mm-status-dot" />
                            {map.isCurrentlyActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {map.description && <p className="mm-map-desc">{map.description}</p>}
                        <div className="mm-map-stats">
                          <span className="mm-map-stat"><Eye size={12} />{map.visitCount || 0} visits</span>
                          <span className="mm-map-stat"><MapPinned size={12} />{map.markersCount || 0} markers</span>
                          <span className="mm-map-stat"><Calendar size={12} />{new Date(map.startTime).toLocaleDateString()}</span>
                        </div>
                        <div className="mm-map-actions">
                          <button className="mm-btn mm-btn--primary mm-btn--sm" onClick={() => setViewingMap(map)}>
                            <Map size={13} />View
                          </button>
                          <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => {
                            const url = qrAPI.getQRCodeUrl(map.base44Code, { size: 400, download: true });
                            window.open(url, '_blank');
                          }}>
                            <QrCode size={13} />QR
                          </button>
                          <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => setEditingMap(map)}>
                            <Pencil size={13} />Edit
                          </button>
                          <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => navigate(`/analytics/${map.id}`)}>
                            <BarChart2 size={13} />Analytics
                          </button>
                          {map.isOwner !== false && (
                            <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => setNotificationsMap(map)}>
                              <Bell size={13} />Notify
                            </button>
                          )}
                          {map.isOwner !== false && (
                            <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => setManagingCollaborators(map)}>
                              <Users size={13} />Access
                            </button>
                          )}
                          {events.length > 0 && (
                            <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => setAssigningMap(map)}>
                              <FolderOpen size={13} />Assign
                            </button>
                          )}
                          {map.isOwner !== false && (
                            <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => handleArchiveUnassignedMap(map)}
                              title="Archive map">
                              <Package size={13} />Archive
                            </button>
                          )}
                          {map.isOwner !== false && (
                            <button className="mm-btn mm-btn--danger mm-btn--sm" onClick={async () => {
                              if (!window.confirm(`Delete "${map.title}"?`)) return;
                              await deleteMap(map.id);
                              setUnassignedMaps(prev => prev.filter(x => x.id !== map.id));
                              refreshMaps();
                            }}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                archivedUnassignedMaps.length === 0 ? (
                  <div className="mm-empty" style={{ padding: '1.5rem 0' }}>
                    <p style={{ color: '#94A3B8', fontSize: '0.875rem' }}>No archived unassigned maps.</p>
                  </div>
                ) : (
                  <div className="mm-maps-list">
                    {archivedUnassignedMaps.map(map => (
                      <div key={map.id} className="mm-map-card mm-map-card--archived">
                        <div className="mm-map-card-header">
                          <h4 className="mm-map-title">{map.title}</h4>
                          <span className="mm-badge" style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                            Archived {new Date(map.archivedAt).toLocaleDateString()}
                          </span>
                        </div>
                        {map.description && <p className="mm-map-desc">{map.description}</p>}
                        <div className="mm-map-stats">
                          <span className="mm-map-stat"><Eye size={12} />{map.visitCount || 0} visits</span>
                          <span className="mm-map-stat"><MapPinned size={12} />{map.markersCount || 0} markers</span>
                          <span className="mm-map-stat"><Calendar size={12} />{new Date(map.startTime).toLocaleDateString()}</span>
                        </div>
                        <div className="mm-map-actions">
                          {map.isOwner !== false && (
                            <button className="mm-btn mm-btn--ghost mm-btn--sm"
                              onClick={() => handleRestoreUnassignedMap(map)}>
                              <RefreshCw size={13} />Restore
                            </button>
                          )}
                          {map.isOwner !== false && (
                            <button className="mm-btn mm-btn--danger mm-btn--sm" onClick={async () => {
                              if (!window.confirm(`Delete "${map.title}"? This cannot be undone.`)) return;
                              await deleteMap(map.id);
                              setArchivedUnassignedMaps(prev => prev.filter(x => x.id !== map.id));
                              refreshMaps();
                            }}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {editingMap && (
            <EditMapModal map={editingMap} isOpen={!!editingMap}
              onClose={() => setEditingMap(null)}
              onSave={() => { setEditingMap(null); fetchUnassigned(); }} />
          )}
          {viewingMap && (
            <MapDetailModal map={viewingMap} isOpen={!!viewingMap}
              onClose={() => setViewingMap(null)} />
          )}
          {managingCollaborators && (
            <CollaboratorsManager mapId={managingCollaborators.id}
              isOwner={managingCollaborators.isOwner !== false}
              onClose={() => setManagingCollaborators(null)} />
          )}
          {assigningMap && (
            <AssignEventModal map={assigningMap} events={events} isOpen={!!assigningMap}
              onClose={() => setAssigningMap(null)} onAssign={handleAssignMap} />
          )}
          {managingEventAccess && (
            <EventCollaboratorsModal event={managingEventAccess} isOpen={!!managingEventAccess}
              onClose={() => setManagingEventAccess(null)} />
          )}
          {/* ── Notifications modal for unassigned maps ── */}
          {notificationsMap && (
            <NotificationsModal
              map={notificationsMap}
              onClose={() => setNotificationsMap(null)} />
          )}
        </>
      ) : (
        archivedEvents.length === 0 ? (
          <div className="mm-empty">
            <div className="mm-empty-icon"><Package size={24} /></div>
            <h4>No archived events</h4>
            <p>Archived events will appear here.</p>
          </div>
        ) : (
          <div className="mm-folders-grid">
            {archivedEvents.map(event => (
              <div key={event._id} className="mm-folder mm-folder--archived">
                <div className="mm-folder-accent" style={{ background: event.coverColor || '#94A3B8' }} />
                <div className="mm-folder-body">
                  <h3 className="mm-folder-title">{event.title}</h3>
                  <p className="mm-folder-desc">
                    {CATEGORY_META[event.category]?.label} · {event.mapCount || 0} maps
                  </p>
                  <p className="mm-folder-archived-date">
                    Archived {new Date(event.archivedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="mm-folder-actions">
                  <button className="mm-btn mm-btn--ghost mm-btn--sm" onClick={() => unarchiveEvent(event._id)}>
                    Restore
                  </button>
                  <button className="mm-icon-btn mm-icon-btn--danger" onClick={() => handleDelete(event)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <CreateEventModal isOpen={showCreateEvent}
        onClose={() => setShowCreateEvent(false)} onCreate={createEvent} />
    </div>
  );
};

export default MyMaps;